<template>
  <div id="userGroupView">
    <h1>{{ $t('privatePage.usergroups_header') }}</h1>
    <b-row>
    <b-col lg="4">
      <span class="fieldHeader">{{$t('privatePage.customerType_reseller')}}:</span>
    </b-col>
    </b-row>
    <b-row>
    <b-col lg="4" style="padding-right: 0px;">
    <b-form-group id="resellerGroup">
      <b-form-select id="resellerSelect"
                    v-model="resellerGroupId"
                    :options="resellerGroups"
                    value-field="id"
                    text-field="name"
                    v-on:change="selectionChanged" >
      </b-form-select>
    </b-form-group>
    </b-col>
    <b-col lg="2">
      <b-button @click="openTransactionModal" v-b-modal.transactionHistoryModal>{{ $t('privatePage.see_transactions') }}</b-button>
    </b-col>
    <b-col lg="2">
      <div class="pull-right">
        <b-form-checkbox id="switchShowInactive" v-model="showInactive" switch :value="true" :unchecked-value="false" @change="onToggleInactive">
          {{ $t('privatePage.show_inactive') }}
        </b-form-checkbox>
      </div>
    </b-col>
    </b-row>

    <b-modal id="transactionHistoryModal" size="lg" hide-footer ref="transactionHistoryModal" no-close-on-backdrop>
      <b-row>
      <b-col>
        <b-card :header="$t('privatePage.transaction_history')">
          <b-row>
            <b-col lg="5">
              <span class="fieldHeader">{{$t('privatePage.transaction_start')}}:</span>
            </b-col>
            <b-col lg="5">
              <span class="fieldHeader">{{$t('privatePage.transaction_end')}}:</span>
            </b-col>
          </b-row>
          <b-row>
            <b-col lg="5" style="padding-right: 0px;">
              <b-form-group id="transactionHistoryStart" style="padding-left: 0px;">
                <b-form-input id="transactionStart"
                              v-model="transactionStart"
                              type="date">
                </b-form-input>
              </b-form-group>
            </b-col>
            <b-col lg="5" style="padding-right: 0px;">
              <b-form-group id="transactionHistoryEnd" style="padding-left: 0px;">
                <b-form-input id="transactionEnd"
                              v-model="transactionEnd"
                              type="date">
                </b-form-input>
              </b-form-group>
            </b-col>
            <b-col>
              <b-button @click="getAllTransactions()">{{ $t('search') }}</b-button>
            </b-col>
          </b-row>
        </b-card>
      </b-col>
    </b-row>
    <b-row style="margin-top: 10px;">
        <b-col>
          <b-card :header="$t('result')">
            <transaction-table :creditTransactions="creditTransactions" />
          </b-card>
        </b-col>
      </b-row>
    </b-modal>

    <b-modal id="userEditorModal" v-model="showUserModal" hide-footer ref="userEditorModal" no-close-on-backdrop>
      <template #title>
        <h1 class="modalHeader">{{$t('privatePage.user_edit')}}</h1>
      </template>
      <user-editor :current-user="modalInfo"
                   :languages="languages"
                   ref="userEditorForm"
                   v-on:formSubmitted="formSubmitted"
                   v-on:formCancelled="formCancelled"
                   v-on:userDeleteAttempt="userDeleteAttempt" >
      </user-editor>
    </b-modal>

    <b-row>
    <b-col lg="8">
    <span class="fieldHeader">{{$t('privatePage.customers_header')}}:</span>
    <EasyDataTable
      :headers="tableFields"
      :items="filteredCustomerGroups"
      :alternating="true"
      :rows-per-page="10000"
      :hide-rows-per-page="true"
    >
      <template #item-isActive="item">
        <span v-show="item.isActive" class="fa fa-check"></span>
      </template>

      <template #item-show_details="item">
        <b-button @click.stop="groupClicked(item)"
                  size="sm"
                  class="fa fa-edit"
                  v-show="hasGroupEditRights(item)">
        </b-button>
      </template>

      <template #expand="item">
        <b-card class="user-card mt-2 mb-2">

          <EasyDataTable
            :headers="detailTableFields"
            :items="item.members"
            :alternating="true"
            table-class-name="userTable"
            :rows-per-page="10000"
            :hide-rows-per-page="true"
          >
            <template #item-buttons="row">
              <b-button @click.stop="openEditUser(row, item)"
                        size="sm"
                        class="fa fa-edit"
                        v-show="hasEditRights(item)">
              </b-button>
            </template>
            <template #item-isActive="row">
              <span v-show="row.isActive" class="fa fa-check"></span>
            </template>
            <template #item-latestLoginDate="row">
              <span>{{ formatDate(row.latestLoginDate) }}</span>
            </template>
          </EasyDataTable>

          <b-button class="mt-2" @click.stop="openAddUser(item)" v-show="hasEditRights(item)">
            {{ $t('privatePage.usergroup_add_user') }}
          </b-button>

        </b-card>
      </template>

    </EasyDataTable>
    </b-col>
    </b-row>

    <b-button to="group" @click.stop="beforeAddGroup" class="btn-secondary mt-2" v-show="hasResellerPermissions">
      {{ $t('privatePage.usergroup_add_new') }}
    </b-button>

  </div>
</template>

<script>
import moment from 'moment'
import { mapGetters, mapActions } from 'vuex'
import UserEditor from '../../components/users/usereditor'
import TransactionTable from '../../components/users/transactiontable'

let date = new Date()
date.setDate(date.getDate())
let transactionEndString = date.toLocaleDateString('sv-SE')

date.setDate(date.getDate() - 30)
let transactionStartString = date.toLocaleDateString('sv-SE')

export default {
  components: {
    UserEditor,
    TransactionTable
  },
  computed: {
    ...mapGetters([
      'userId',
      'hasSupplierPermissions',
      'hasResellerPermissions',
      'hasCustomerPermissions',
      'defaultCulture',
      'languages'
    ]),
    ...mapGetters('userGroupsModule', [
      'currentGroup',
      'defaultResellerGroupId',
      'resellerGroups',
      'customerGroups',
      'defaultCustomerTypeId',
      'defaultCustomerTypeName'
    ]),

    tableFields: function() {
      return [
        { value: 'name', text: this.$t('name'), sortable: true },
        {
          value: 'organisationNumber',
          text: this.$t('privatePage.orgNumber')
        },
        { value: 'credits', text: this.$t('privatePage.credits_header') },
        {
          value: 'userGroupType',
          text: this.$t('privatePage.customerType_header')
        },
        { value: 'isActive', text: this.$t('privatePage.isActive') },
        { value: 'show_details', text: ' ', tdClass: 'rightAlignedField' }
      ]
    },
    filteredCustomerGroups: function() {
      if (this.showInactive) {
        return this.customerGroups
      } else {
        return [...this.customerGroups].filter(i => i.isActive)
      }
    },
    detailTableFields: function() {
      return [
        {
          value: 'userName',
          text: this.$t('privatePage.username'),
          sortable: true
        },
        { value: 'email', text: this.$t('privatePage.email') },
        {
          value: 'latestLoginDate',
          text: this.$t('privatePage.loginLatest_header')
        },
        { value: 'isActive', text: this.$t('privatePage.isActive') },
        { value: 'buttons', text: ' ' }
      ]
    }
  },
  data() {
    return {
      creditTransactions: [],
      transactionStart: transactionStartString,
      transactionEnd: transactionEndString,
      resellerGroupId: null,
      showInactive: false,
      modalInfo: {
        isNew: false,
        userId: '',
        userName: '',
        email: '',
        password: '',
        password2: '',
        languageId: '',
        isActive: false,
        savePassword: false
      },
      userInfo: {
        userName: '',
        email: '',
        password: '',
        groupId: '',
        languageId: '',
        isActive: false,
        savePassword: false
      },
      currentUser: null,
      showUserModal: false
    }
  },
  methods: {
    ...mapActions({
      getLanguages: 'getLanguages',
      showSuccessMessage: 'showSuccessMessage',
      showErrorMessage: 'showErrorMessage',
      setSelectedCulture: 'setSelectedCulture'
    }),
    ...mapActions('userGroupsModule', {
      getMyCustomerGroups: 'getMyCustomerGroups',
      selectGroup: 'selectGroup',
      deleteUser: 'deleteUser',
      addUser: 'addUser',
      updateUser: 'updateUser',
      getMyResellerGroups: 'getMyResellerGroups',
      getCreditTransactions: 'getCreditTransactions'
    }),
    ...mapActions('testMethodsModule', {
      getAllTestMethods: 'getAllTestMethods'
    }),
    formatDate(value) {
      return moment(value).format('YYYY-MM-DD - hh:mm:ss')
    },
    selectionChanged: function(evt) {
      this.creditTransactions = []
      this.getMyCustomerGroups(evt)
    },
    hasEditRights: function(item) {
      return (
        this.hasSupplierPermissions ||
        (this.hasResellerPermissions &&
          item.userGroupTypeId === this.defaultCustomerTypeId)
      )
    },
    hasGroupEditRights: function(item) {
      return (
        this.hasSupplierPermissions ||
        (this.hasResellerPermissions &&
          item.userGroupTypeId === this.defaultCustomerTypeId)
      )
    },
    onToggleInactive: function(value) {
      console.log('value: ', value)
    },
    openAddUser: function(item) {
      this.userInfo.groupId = item.id
      this.modalInfo.isNew = true
      this.modalInfo.savePassword = true
      this.modalInfo.userName = ''
      this.modalInfo.email = ''
      this.modalInfo.languageId = ''
      this.modalInfo.password = ''
      this.modalInfo.password2 = ''
      this.modalInfo.isActive = true
      this.currentUser = item

      this.showUserModal = true
    },
    openEditUser: function(item, group) {
      this.modalInfo.userId = item.id
      this.modalInfo.isNew = false
      this.modalInfo.savePassword = false
      this.modalInfo.userName = item.userName
      this.modalInfo.email = item.email
      this.modalInfo.languageId = item.languageId
      this.modalInfo.password = ''
      this.modalInfo.password2 = ''
      this.modalInfo.isActive = item.isActive
      this.currentUser = item

      this.showUserModal = true

      this.selectGroup(group)
        .then(() => {})
        .catch(() => {})
    },
    userDeleteAttempt: function(evt) {
      this.deleteUser({
        group: this.currentGroup,
        user: this.currentUser
      })
        .then(result => {
          window.alert(this.$t('privatePage.userDelete_message'))
          this.showUserModal = false
        })
        .catch(error => {
          var userEditorForm = this.$refs.userEditorForm
          userEditorForm.showWarning(error)
        })
    },
    formSubmitted: function(evt) {
      this.userInfo.userName = this.modalInfo.userName
      this.userInfo.email = this.modalInfo.email
      this.userInfo.password = this.modalInfo.password
      this.userInfo.isActive = this.modalInfo.isActive
      this.userInfo.languageId = this.modalInfo.languageId

      if (this.modalInfo.isNew) {
        this.addUser(this.userInfo)
          .then(result => {
            if (result.ok === true) {
              this.showUserModal = false
            } else {
              var userEditorForm = this.$refs.userEditorForm
              userEditorForm.showWarning(result.message)
            }
          })
          .catch(() => {})
      } else {
        if (this.currentUser) {
          this.userInfo.savePassword = this.modalInfo.savePassword

          this.updateUser({
            currentUser: this.currentUser,
            newUser: this.userInfo
          })
            .then(result => {
              if (result.ok === true) {
                this.showUserModal = false
              } else {
                var userEditorForm = this.$refs.userEditorForm
                userEditorForm.showWarning(result.message)
              }
            })
            .catch(() => {})
        }
      }
    },
    formCancelled: function() {
      this.showUserModal = false
    },
    groupClicked: function(group) {
      const self = this
      this.selectGroup(group)
        .then(() => {
          self.$router.push({ path: '/private/group' })
        })
        .catch(() => {})
    },
    beforeAddGroup: function() {
      this.selectGroup({})
        .then(() => {})
        .catch(() => {})
    },
    getAllTransactions() {
      this.getCreditTransactions({
        groupId: this.resellerGroupId,
        dateFrom: this.transactionStart,
        dateTo: this.transactionEnd
      }).then(response => {
        this.creditTransactions = response.length ? response : []
      })
    }
  },
  created() {
    this.getMyResellerGroups().then(() => {
      this.resellerGroupId = this.defaultResellerGroupId
      this.getMyCustomerGroups(this.resellerGroupId)
    })
  }
}
</script>
